import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { GetBaseURL } from "./utils/urlUtils";
import {
  Lock,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle,
  AlertCircle,
  Users,
} from "lucide-react";
import Logo from "../assets/KavoaLogo.png";

interface InviteInfo {
  name: string;
  email: string;
  firmName: string;
}

interface AcceptInviteProps {
  onAccepted?: (token: string, user: any) => void;
}

const AcceptInvite: React.FC<AcceptInviteProps> = ({ onAccepted }) => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [invalidToken, setInvalidToken] = useState(false);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Fetch invite info on mount
  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await axios.get(
          `${GetBaseURL()}/api/users/invite-info/${token}`
        );
        setInviteInfo(res.data);
      } catch {
        setInvalidToken(true);
      } finally {
        setLoadingInfo(false);
      }
    };
    if (token) fetchInfo();
    else setLoadingInfo(false);
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");

    if (password.length < 6) {
      setErrorMessage("Password must be at least 6 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    setSubmitting(true);

    try {
      const res = await axios.post(
        `${GetBaseURL()}/api/users/accept-invite/${token}`,
        { password }
      );

      setSuccess(true);

      // If a callback is provided, log the user in automatically
      if (onAccepted && res.data.token) {
        setTimeout(() => {
          onAccepted(res.data.token, res.data.user);
        }, 2000);
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data) {
        setErrorMessage(
          err.response.data.msg || "Something went wrong. Please try again."
        );
      } else {
        setErrorMessage("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loadingInfo) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-emerald-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Invalid/expired token
  if (invalidToken || !token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
          <div className="flex justify-center mb-8">
            <img src={Logo} alt="Kavoa" className="h-10" />
          </div>
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-50">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">
              Invitation expired
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              This invitation link is invalid or has expired. Please ask your
              administrator to send a new invitation.
            </p>
            <button
              onClick={() => navigate("/")}
              className="w-full rounded-xl py-3 font-bold text-white shadow-md hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] transition-all"
              style={{ backgroundColor: "#0F5132" }}
            >
              Go to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img src={Logo} alt="Kavoa" className="h-10" />
        </div>

        {success ? (
          /* ── Success State ── */
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">
              Welcome to {inviteInfo?.firmName}!
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              Your account is active. Redirecting you to your dashboard...
            </p>
            <div className="flex justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
            </div>
          </div>
        ) : (
          /* ── Form State ── */
          <>
            {/* Invite context */}
            {inviteInfo && (
              <div className="mb-6 p-4 bg-emerald-50 border border-emerald-100 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-emerald-700" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-emerald-800">
                      You've been invited to join
                    </p>
                    <p className="text-lg font-bold text-emerald-900">
                      {inviteInfo.firmName}
                    </p>
                    <p className="text-xs text-emerald-600 mt-1">
                      {inviteInfo.email}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-slate-800 mb-1">
                Set Your Password
              </h2>
              <p className="text-sm text-slate-500">
                Hi {inviteInfo?.name}, create a password to activate your
                account.
              </p>
            </div>

            {/* Error banner */}
            {errorMessage && (
              <div className="mb-5 flex items-start gap-3 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-xl p-3.5">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <span>{errorMessage}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Password */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-slate-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="At least 6 characters"
                    className="w-full rounded-xl border border-slate-200 pl-11 pr-11 py-3 text-sm outline-none transition-all focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-[18px] h-[18px]" />
                    ) : (
                      <Eye className="w-[18px] h-[18px]" />
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-slate-400" />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Re-enter your password"
                    className="w-full rounded-xl border border-slate-200 pl-11 pr-11 py-3 text-sm outline-none transition-all focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-[18px] h-[18px]" />
                    ) : (
                      <Eye className="w-[18px] h-[18px]" />
                    )}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-xl py-3 font-bold text-white shadow-md hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                style={{ backgroundColor: "#0F5132" }}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  "Accept & Create Account"
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default AcceptInvite;
